import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { DemoBadge } from "../components/DemoBadge";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CLIENT_TIERS, CLIENT_HEALTHS } from "@/lib/enumLabels";

const HEALTH_DOT = { strong: "green", stable: "green", at_risk: "yellow", critical: "red" };

/**
 * InlineText — click-to-edit text cell used on the Clients list for HQ
 * location, industry, and annual revenue. Stops propagation so the parent
 * `<Link>` on the row (or elsewhere) never navigates into the client
 * detail when the partner is inline-editing a value.
 *
 * Iter 85.33 · Portfolio-view inline editing. Values, validation, and
 * canonical fields mirror the ClientDetail quick-controls surface
 * (`hq_location`, `industry`, `annual_revenue`, `tier`,
 * `relationship_health`, `relationship_owner_user_id`).
 */
function InlineText({ value, onSave, testid, type = "text", format, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  const commit = async () => {
    setEditing(false);
    const raw = String(draft ?? "").trim();
    let next;
    if (type === "number") {
      next = raw === "" ? null : Number(raw);
      if (raw !== "" && Number.isNaN(next)) {
        toast.error("Must be a number");
        return;
      }
    } else {
      next = raw === "" ? null : raw;
    }
    if ((next ?? "") === (value ?? "")) return;
    try { await onSave(next); } catch { /* onSave surfaces its own toast */ }
  };
  const display = value == null || value === ""
    ? "—"
    : (format ? format(value) : String(value));
  if (editing) {
    return (
      <Input
        autoFocus
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { setEditing(false); setDraft(value ?? ""); }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`rounded-none bg-secondary/40 h-7 text-xs px-2 ${className}`}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true); }}
      className={`text-left w-full hover:text-primary transition-colors ${className}`}
      data-testid={testid}
    >
      {display}
    </button>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", industry: "", company_size: "", hq_location: "", website: "",
    tier: "growth", relationship_health: "stable", relationship_owner: "",
    annual_revenue: "", strategic_context: "", known_priorities: "", known_pain_points: "",
  });

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);
  // Iter 85.33 · Owner picker needs the canonical team list. Fetched once
  // on mount because the list rarely changes during a session, matching
  // ClientDetail's usage pattern.
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  // Iter 85.33 · Optimistic patch for inline cell edits. Sends the same
  // canonical PUT /clients/{id} that ClientDetail uses so both surfaces
  // share one data model. Reverts on failure and surfaces a toast.
  const patchClient = async (id, patch) => {
    const prev = clients;
    setClients((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    try {
      const { data } = await api.put(`/clients/${id}`, patch);
      // Server may enrich the response (e.g. relationship_owner display
      // string when owner_user_id changed) — merge the authoritative
      // record back so both cells stay in sync.
      setClients((cs) => cs.map((c) => (c.id === id ? data : c)));
      toast.success("Partner organization updated");
    } catch (e) {
      setClients(prev);
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Partner organization name required");
    try {
      await api.post("/clients", {
        ...form,
        annual_revenue: form.annual_revenue ? parseFloat(form.annual_revenue) : null,
        known_priorities: form.known_priorities.split(",").map((s) => s.trim()).filter(Boolean),
        known_pain_points: form.known_pain_points.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setOpen(false);
      setForm({ name: "", industry: "", company_size: "", hq_location: "", website: "", tier: "growth", relationship_health: "stable", relationship_owner: "", annual_revenue: "", strategic_context: "", known_priorities: "", known_pain_points: "" });
      toast.success("Partner organization created");
      load();
    } catch (e) { toast.error("Failed to create"); }
  };

  const filtered = clients.filter((c) =>
    (tierFilter === "all" || c.tier === tierFilter) &&
    (c.name.toLowerCase().includes(q.toLowerCase()) || (c.industry || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <PageShell size="default">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">Book of Business</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Partners</h1>
          <p className="text-sm text-muted-foreground mt-1">{clients.length} partner organizations · segmented by strategic tier &amp; relationship health.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New partner</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-none border-border bg-card">
            <DialogHeader><DialogTitle className="font-display tracking-tight">New partner organization</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-2"><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Name *</Label>
                <Input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Industry</Label>
                <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Company size</Label>
                <Input value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })} placeholder="e.g. 500-1000 FTE" className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">HQ</Label>
                <Input value={form.hq_location} onChange={(e) => setForm({ ...form, hq_location: e.target.value })} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Tier</Label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Relationship health</Label>
                <Select value={form.relationship_health} onValueChange={(v) => setForm({ ...form, relationship_health: v })}>
                  <SelectTrigger className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_HEALTHS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Relationship owner</Label>
                <Input value={form.relationship_owner} onChange={(e) => setForm({ ...form, relationship_owner: e.target.value })} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Annual revenue (est.)</Label>
                <Input value={form.annual_revenue} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })} type="number" className="rounded-none bg-secondary/40 mt-1" /></div>
              <div className="col-span-2"><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Strategic context</Label>
                <Textarea value={form.strategic_context} onChange={(e) => setForm({ ...form, strategic_context: e.target.value })} rows={3} placeholder="Why they matter to the firm, situation, competitive dynamics…" className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Known priorities (comma-separated)</Label>
                <Input value={form.known_priorities} onChange={(e) => setForm({ ...form, known_priorities: e.target.value })} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Known pain points</Label>
                <Input value={form.known_pain_points} onChange={(e) => setForm({ ...form, known_pain_points: e.target.value })} className="rounded-none bg-secondary/40 mt-1" /></div>
            </div>
            <DialogFooter><Button data-testid="client-submit" onClick={submit} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Create partner organization</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or industry…" className="rounded-none bg-secondary/40 pl-9" />
        </div>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="rounded-none bg-secondary/40 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {CLIENT_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left px-5 py-3 font-normal">Partner</th>
              <th className="text-left px-3 py-3 font-normal">Industry</th>
              <th className="text-left px-3 py-3 font-normal">Tier</th>
              <th className="text-left px-3 py-3 font-normal">Health</th>
              <th className="text-left px-3 py-3 font-normal">Owner</th>
              <th className="text-right px-5 py-3 font-normal">Ann. revenue</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="6" className="px-5 py-12 text-center text-muted-foreground text-xs">No partners yet. Add your first to begin.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`clients-row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Link to={`/clients/${c.id}`} className="hover:text-primary font-medium" data-testid={`client-row-${c.id}`}>{c.name}</Link>
                    <DemoBadge show={!!c.is_demo} />
                  </div>
                  {/* HQ location — inline text editor. Empty string treated as null. */}
                  <div className="text-[0.625rem] font-mono uppercase text-muted-foreground tracking-widest mt-0.5">
                    <InlineText
                      value={c.hq_location}
                      onSave={(v) => patchClient(c.id, { hq_location: v })}
                      testid={`client-list-hq-${c.id}`}
                    />
                  </div>
                </td>
                {/* Industry — inline text editor. */}
                <td className="px-3 py-3 text-xs">
                  <InlineText
                    value={c.industry}
                    onSave={(v) => patchClient(c.id, { industry: v })}
                    testid={`client-list-industry-${c.id}`}
                  />
                </td>
                {/* Tier — dropdown with canonical TIERS choices. */}
                <td className="px-3 py-3 text-xs uppercase font-mono tracking-widest text-primary">
                  <Select
                    value={c.tier || "growth"}
                    onValueChange={(v) => patchClient(c.id, { tier: v })}
                  >
                    <SelectTrigger
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-none bg-transparent border-0 p-0 h-auto text-xs uppercase font-mono tracking-widest text-primary hover:text-primary/80"
                      data-testid={`client-list-tier-${c.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{CLIENT_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                {/* Health — dropdown with canonical HEALTHS choices + traffic-light dot. */}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`dot dot-${HEALTH_DOT[c.relationship_health] || "green"}`} />
                    <Select
                      value={c.relationship_health || "stable"}
                      onValueChange={(v) => patchClient(c.id, { relationship_health: v })}
                    >
                      <SelectTrigger
                        onClick={(e) => e.stopPropagation()}
                        className="rounded-none bg-transparent border-0 p-0 h-auto text-xs uppercase tracking-widest font-mono hover:text-primary"
                        data-testid={`client-list-health-${c.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{CLIENT_HEALTHS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </td>
                {/* Owner — canonical User picker; falls back to legacy display string when unassigned. */}
                <td className="px-3 py-3 text-xs">
                  <Select
                    value={c.relationship_owner_user_id || "__unassigned"}
                    onValueChange={(v) => patchClient(c.id, { relationship_owner_user_id: v === "__unassigned" ? null : v })}
                  >
                    <SelectTrigger
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-none bg-transparent border-0 p-0 h-auto text-xs hover:text-primary"
                      data-testid={`client-list-owner-${c.id}`}
                    >
                      <SelectValue placeholder={c.relationship_owner || "Unassigned"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned">Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`client-list-owner-${c.id}-option-${u.id}`}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                {/* Annual revenue — inline number editor formatted with $ + commas. */}
                <td className="px-5 py-3 text-right data-cell">
                  <InlineText
                    value={c.annual_revenue}
                    onSave={(v) => patchClient(c.id, { annual_revenue: v })}
                    type="number"
                    format={(v) => "$" + Number(v).toLocaleString()}
                    testid={`client-list-revenue-${c.id}`}
                    className="text-right"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
